import { FC, useContext, useEffect, useState } from "react";
import { PartnerStakeProps } from "../../../interfaces";
import { Grid, Typography } from "@material-ui/core";
import PartnerStakeDetailCard from "./PartnerStakeDetailsCard";
import PartnerStakeApproveCard from "./PartnerStakeApproveCard";
import { getSymbol } from "../../../store/Wallet/WalletFunction";
import { useAccount } from "wagmi";
import web3Helper from "../../../services/web3";
import AlertContext from "../../../store/Alert/AlertContext";
import SuccessModal from "../../Modals/SuccessModal";
import WithdrawModal from "./WithdrawModal";

const PartnerStake: FC<PartnerStakeProps> = (props: PartnerStakeProps) => {
  const { pool, poolStakingConfig } = props;

  const { address } = useAccount();
  const { setAlert } = useContext<any>(AlertContext);

  const [showApproveForm, toggleApproveForm] = useState<boolean>(false);
  const [tokenName, setTokenName] = useState<string | null>();
  const [web3Error, toggleWeb3Error] = useState<boolean>(false);
  const [hasStaked, toggleHasStaked] = useState<boolean>(false);

  const [totalStakedUser, setTotalStakedUser] = useState<number>(0);
  const [totalStaked, setTotalStaked] = useState<number>(0);
  const [totalStakedError, toggleTotalStakedError] = useState<boolean>(false);
  const [showSuccess, toggleSuccessModal] = useState<boolean>(false);
  const [showWithdrawModal, toggleWithdrawModal] = useState<boolean>(false);
  const [stakeDetails, setStakeDetails] = useState<any>();
  const [withdrawDetails, setWithdrawDetails] = useState<any>();
  const [withdrawDetailsError, toggleWithdrawDetailsError] = useState<boolean>(false);
  const [stakeDurationMax, setStakeDurationMax] = useState<number>(0);

  const getMaxStakeDuration = (withdrawalRules: any) => {
    let maxDuration = 0;
    withdrawalRules.forEach((rule: any) => {
      if (rule.duration > maxDuration) {
        maxDuration = rule.duration;
      }
    });
    return maxDuration;
  }

  useEffect(() => {
    let maxDuration = getMaxStakeDuration(poolStakingConfig.withdrawalRules);
    setStakeDurationMax(maxDuration)
  }, [poolStakingConfig])

  useEffect(() => {
    web3Helper.getPartnerStakeTotalStaked(poolStakingConfig.networkId, poolStakingConfig.poolStakeAddress, poolStakingConfig.tokenAddress)
    .then((value) => {
      setTotalStaked(value);
      toggleTotalStakedError(false);
    })
    .catch((error: any) => {
      toggleTotalStakedError(true);
      setAlert("Unable to fetch total staked at the moment", "error");
    })
  }, []);

  useEffect(() => {
    getSymbol(poolStakingConfig.networkId, poolStakingConfig.tokenAddress)
    .then((value: string) => {
      setTokenName(value);
      toggleWeb3Error(false);
    })
    .catch(() => {
      toggleWeb3Error(true);
    })
  }, []);

  useEffect(() => {
    if (address) {
      fetchUserStakeDetails(address);
    } else {
      toggleHasStaked(false);
      setTotalStakedUser(0);
      setStakeDetails(null);
    }
  }, [address]);

  useEffect(() => {
    if (hasStaked && address) {
      fetchUserStakeDetails(address);
      web3Helper.getUserWithdrawalDetails(poolStakingConfig.networkId, poolStakingConfig.poolStakeAddress, poolStakingConfig.tokenAddress, address)
      .then((value: any) => {
        setWithdrawDetails(value);
        toggleWithdrawDetailsError(false);
      })
      .catch((error: any) => {
        setWithdrawDetails(null);
        toggleWithdrawDetailsError(true);
      })
    } else {
      setWithdrawDetails(null);
      toggleWithdrawDetailsError(true);
    }
  }, [hasStaked, address]);

  const fetchUserStakeDetails = (address: any) => {
    web3Helper.getUserStake(poolStakingConfig.networkId, poolStakingConfig.poolStakeAddress ,poolStakingConfig.tokenAddress, address)
      .then((value: any) => {
        if (value && value.amountStaked > 0) {
          toggleHasStaked(true);
          setTotalStakedUser(value.amountStaked);
          setStakeDetails(value);
        }
      })
      .catch((error: any) => {
        setAlert('Unable to fetch user stake info', 'error')
        toggleHasStaked(false);
        setTotalStakedUser(0);
        setStakeDetails(null);
      })
  }

  return (
    <Grid container direction="column" xs={12} style={{ marginTop: "10px" }}>
      <Grid item>
        <Typography variant="h6" gutterBottom style={{ margin: "10px 0 10px 0"}}>
          Farm {tokenName} to earn
        </Typography>
      </Grid>
      <Grid item>
        {
          showApproveForm
            ? <PartnerStakeApproveCard pool={pool} stakeConfig={poolStakingConfig} tokenName={tokenName} toggleView={() => toggleApproveForm(false)} hasStaked={hasStaked} toggleHasStaked={(value: boolean) => toggleHasStaked(value)} address={address} setTotalStaked={(value: number) => setTotalStaked((prev) => prev + value)} setTotalStakedUser={(value: number) => setTotalStakedUser((prev) => prev + value)} toggleSuccessModal={() => toggleSuccessModal(true)} totalStaked={totalStaked} maxStakeDuration={stakeDurationMax} />
            : <PartnerStakeDetailCard pool={pool} stakeConfig={poolStakingConfig} tokenName={tokenName} toggleView={() => toggleApproveForm(true)} hasStaked={hasStaked} toggleHasStaked={(value: boolean) => toggleHasStaked(value)} totalStaked={totalStaked} totalStakedUser={totalStakedUser} totalStakedError={totalStakedError} showWithdraw={() => toggleWithdrawModal(true)} maxStakeDuration={stakeDurationMax} />
        }
      </Grid>
      {
        showSuccess && (
          <SuccessModal open={showSuccess} setOpen={toggleSuccessModal} tokenName={tokenName} />
        )
      }
      {
        showWithdrawModal && (
          <WithdrawModal open={showWithdrawModal} setOpen={toggleWithdrawModal} tokenName={tokenName} contractAddress={poolStakingConfig.poolStakeAddress} stakeDetails={stakeDetails} stakeDuration={stakeDurationMax} toggleHasStaked={(value: boolean) => toggleHasStaked(value)} hasPenalty={poolStakingConfig.earlyWithdrawalPenalty} withdrawDetails={withdrawDetails} withdrawDetailsError={withdrawDetailsError} />
        )
      }
    </Grid>
  );
}

export default PartnerStake;